// src/store/useFormStore.ts
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { PostFormPayload } from '@app/api/routes/mce/forms.types';
import { userScopedStorage } from './userScopedStorage';

type FormId = number | string; // number pour les forms existants, string pour les drafts (draft-1, draft-2, etc.)
type ListForms = Record<FormId, PostFormPayload>;

// Paliers de nombre de formulaires après lesquels on affiche la modale de feedback
export const FEEDBACK_FORMS_MILESTONES = [1, 5, 20] as const;

export type FormState = {
  listForms: ListForms;
  isFilling: boolean;
  draftCounter: number;
  savedDrafts: string[]; // Liste des brouillons sauvegardés
  // Map id -> titre des formulaires publiés. On indexe par id pour pouvoir
  // exclure le formulaire en cours d'édition de la vérification d'unicité,
  // sans dépendre d'un instantané du titre original (qui pouvait être absent
  // lors de l'édition d'un brouillon ou non encore chargé sur un form existant).
  formTitles: Record<string | number, string>;
  // Compteur utilisé pour déclencher des feedbacks (modale, etc.)
  pendingFeedbackFormsCount: (typeof FEEDBACK_FORMS_MILESTONES)[number] | null;

  // Actions list
  getFormEntryValues: (formId: FormId | undefined) => Partial<PostFormPayload>;
  updateFieldValue: (formId: FormId, values: PostFormPayload) => void;
  generateDraftId: () => string;
  removeDraft: (draftId: string) => void;
  saveDraftToList: (draftId: string) => void;
  getSavedDrafts: () => Array<{ id: string; data: PostFormPayload }>;
  setPendingFeedbackFormsCount: (count: (typeof FEEDBACK_FORMS_MILESTONES)[number] | null) => void;
  clearPendingFeedbackFormsCount: () => void;
  setFormTitles: (titles: Record<string | number, string>) => void;
  checkTitleExists: (title: string, currentFormId?: string | number) => boolean;
};

// Valeurs initiales réinitialisables (hors actions), partagées entre la création du store et resetFormStore.
const INITIAL_STATE = {
  listForms: {},
  isFilling: false,
  draftCounter: 0,
  savedDrafts: [],
  formTitles: {},
  pendingFeedbackFormsCount: null
} satisfies Partial<FormState>;

export const useFormStore = create<FormState>()(
  devtools(
    persist(
      (set, get) => ({
        ...INITIAL_STATE,

        /**
         * Génère un nouveau draftId unique
         */
        generateDraftId: () => {
          const newCounter = get().draftCounter + 1;
          set({ draftCounter: newCounter });
          return `draft-${newCounter}`;
        },

        /**
         * Met à jour la map id -> titre des formulaires publiés.
         */
        setFormTitles: (titles: Record<string | number, string>) =>
          set(() => ({
            formTitles: titles
          })),

        /**
         * Vérifie si un titre existe déjà (insensible à la casse),
         * en excluant le formulaire en cours d'édition (via `currentFormId`).
         */
        checkTitleExists: (title: string, currentFormId?: string | number) => {
          const normalizedTitle = title.trim().toLowerCase();
          if (!normalizedTitle) return false;
          const titles = get().formTitles;
          return Object.entries(titles).some(([id, existingTitle]) => {
            if (currentFormId !== undefined && String(id) === String(currentFormId)) {
              return false;
            }
            return existingTitle.trim().toLowerCase() === normalizedTitle;
          });
        },

        /**
         * Met à jour le compteur de formulaires utilisés pour le feedback
         */
        setPendingFeedbackFormsCount: (count: (typeof FEEDBACK_FORMS_MILESTONES)[number] | null) =>
          set(() => ({
            pendingFeedbackFormsCount: count
          })),

        /**
         * Réinitialise le compteur de feedback (une fois consommé)
         */
        clearPendingFeedbackFormsCount: () =>
          set(() => ({
            pendingFeedbackFormsCount: null
          })),

        /**
         * Supprime un draft du store
         */
        removeDraft: (draftId: string) =>
          set((state) => {
            const { [draftId]: _, ...rest } = state.listForms;
            // Gérer le cas où savedDrafts n'est pas un tableau
            const draftsArray = Array.isArray(state.savedDrafts) ? state.savedDrafts : [];
            const newSavedDrafts = draftsArray.filter(id => id !== draftId);
            return { 
              listForms: rest,
              savedDrafts: newSavedDrafts
            };
          }),

        /**
         * Marque un brouillon comme sauvegardé
         */
        saveDraftToList: (draftId: string) =>
          set((state) => {
            // Gérer le cas où savedDrafts n'est pas un tableau
            const draftsArray = Array.isArray(state.savedDrafts) ? state.savedDrafts : [];
            if (draftsArray.includes(draftId)) {
              return state;
            }
            return { savedDrafts: [...draftsArray, draftId] };
          }),

        /**
         * Récupère tous les brouillons sauvegardés
         */
        getSavedDrafts: () => {
          const state = get();
          // Gérer le cas où savedDrafts n'est pas un tableau (migration depuis Set ou undefined)
          const draftsArray = Array.isArray(state.savedDrafts) ? state.savedDrafts : [];
          return draftsArray
            .map(draftId => ({
              id: draftId,
              data: state.listForms[draftId]
            }))
            .filter(draft => draft.data); // Filtrer les brouillons sans données
        },

        updateFieldValue: (formId: FormId, values: PostFormPayload) => {

          return set((state) => {
            return {
              listForms: {
                ...state.listForms,
                [formId]: values
              },
              isFilling: true
            };
          });
        },

        getFormEntryValues: (formId: FormId | undefined) => {
          if (!formId) {
            return {};
          }
          return get().listForms[formId] || {};
        }
      }),
      {
        // Clé distincte de `useFormEntriesStore` ('form-entries-storage')
        // pour éviter la collision historique (cf. CW-21966).
        name: 'form-storage',
        storage: userScopedStorage<Pick<FormState,
          'listForms' | 'isFilling' | 'draftCounter' | 'savedDrafts' | 'pendingFeedbackFormsCount'
        >>(),
        skipHydration: true,
        partialize: (state) => ({
          listForms: state.listForms,
          isFilling: state.isFilling,
          draftCounter: state.draftCounter,
          savedDrafts: state.savedDrafts,
          pendingFeedbackFormsCount: state.pendingFeedbackFormsCount,
          // Exclure formTitles de la persistance : rechargé depuis l'API à chaque session
        })
      }
    ),
    {
      name: 'AppStore', // Nom affiché dans Redux DevTools
      enabled: true
    }
  )
);

/** Reset complet du store en mémoire (à appeler au logout). */
export const resetFormStore = () => {
  useFormStore.setState(INITIAL_STATE);
};
